import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Sun, Thermometer, BatteryCharging, Droplets, Cpu, Zap, Check, MapPin } from 'lucide-react';
import { SOLUTIONS, BENEFITS, STEPS, COMPANY } from '../mock';
import PartnerBanner from '../components/PartnerBanner';
import CTASection from '../components/CTASection';

const ICONS = { Sun, Thermometer, BatteryCharging, Droplets, Cpu, Zap };

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-grad-hero overflow-hidden">
        <img src="/logos/logo-green.png" alt="" aria-hidden="true" className="absolute -left-32 top-1/4 w-[460px] opacity-[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <p className="eyebrow">Panneaux solaires · Stockage · Pompes à chaleur</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.02] text-gray-900">
              Votre transition <br/>
              énergétique, <span className="italic-accent">notre expertise.</span>
            </h1>
            <p className="mt-7 text-gray-600 max-w-xl leading-relaxed">
              Installation de panneaux photovoltaïques, pompes à chaleur, batteries de stockage et boilers thermodynamiques en Suisse romande. Suisse Energies Renouvelables offre des solutions énergétiques durables pour plus de confort et d'économies pour votre foyer.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium">Demander un devis <ArrowUpRight className="w-4 h-4" /></Link>
              <Link to="/solutions" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium bg-white">Découvrir nos solutions <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[{n:'100%',t:'Suisse romande'},{n:'7',t:'cantons couverts'},{n:'70%',t:"d'économies"}].map((s,i)=>(
                <div key={i}>
                  <div className="font-display text-3xl md:text-4xl text-[#125082] whitespace-nowrap">{s.n}</div>
                  <div className="text-xs text-gray-500 mt-1">{s.t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5]">
              <img src="/photos/panneaux-solaires.jpg" alt="Installation solaire en Suisse" className="w-full h-full object-cover" />
              <div className="absolute top-5 right-5 bg-white/95 backdrop-blur px-4 py-2 rounded-full text-[11px] tracking-widest uppercase font-semibold text-[#125082]">Installateur de confiance</div>
            </div>
            <div className="absolute -bottom-8 -left-6 lg:-left-10 bg-[#125082] text-white p-6 rounded-2xl shadow-xl max-w-[260px]">
              <p className="text-[11px] tracking-widest uppercase text-[#80b23f] font-semibold mb-2">Cantons</p>
              <p className="text-sm leading-relaxed">{COMPANY.cantons.join(' · ')}</p>
              <MapPin className="absolute right-5 top-5 w-5 h-5 text-[#80b23f]" />
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div>
              <p className="eyebrow">Nos solutions</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl text-gray-900">Des systèmes complets pour votre <span className="italic-accent">indépendance énergétique.</span></h2>
            </div>
            <Link to="/solutions" className="inline-flex items-center gap-1 text-[#125082] font-medium hover:gap-2 transition-all">Voir toutes les solutions <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SOLUTIONS.slice(0,4).map(s => {
              const Icon = ICONS[s.icon] || Sun;
              return (
                <Link to={`/solutions/${s.slug}`} key={s.slug} className="solution-card group relative bg-white rounded-2xl overflow-hidden border border-gray-200 flex flex-col">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-8 h-8 rounded-full bg-[#80b23f]/15 flex items-center justify-center"><Icon className="w-4 h-4 text-[#80b23f]" /></span>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-gray-500 font-semibold line-clamp-1">{s.eyebrow}</span>
                    </div>
                    <h3 className="font-display text-xl text-gray-900 mb-2">{s.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{s.short}</p>
                    <span className="text-sm font-medium text-[#125082] inline-flex items-center gap-1 group-hover:gap-2 transition-all">En savoir plus <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <PartnerBanner />

      {/* BENEFITS */}
      <section className="py-24 bg-[#f1f1f1]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 mb-14">
            <div>
              <p className="eyebrow">Pourquoi maintenant</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight text-gray-900">Pourquoi démarrer votre transition <span className="italic-accent">énergétique ?</span></h2>
            </div>
            <p className="text-gray-600 lg:pt-12 leading-relaxed">La transition énergétique s'impose aujourd'hui comme un axe stratégique majeur pour maîtriser durablement vos dépenses, accroître la valeur de votre patrimoine immobilier et optimiser votre confort au quotidien. Avec la Stratégie Energétique 2050 et les aides cantonales, les conditions sont particulièrement favorables.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white p-7 rounded-2xl border border-gray-200 hover:border-[#80b23f] transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-full bg-[#125082] text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>
                  <span className="step-number text-xl">0{i+1}</span>
                </div>
                <h3 className="font-display text-xl text-gray-900 mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-24 bg-[#125082] text-white relative overflow-hidden on-dark">
        <img src="/logos/logo-white.png" alt="" aria-hidden="true" className="absolute -right-32 top-10 w-[500px] opacity-[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-10 mb-14">
            <div>
              <p className="eyebrow !text-[#80b23f]">Travailler avec nous</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Les 6 grandes étapes <br/><span className="italic-accent">de votre projet.</span></h2>
            </div>
            <p className="text-white/75 lg:pt-12 leading-relaxed">Chaque projet est entièrement géré en interne par les bureaux d'études solaires et chauffage Suisse Energies Renouvelables. Une parfaite maîtrise des délais, des coûts et des performances, sans intermédiaires.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 backdrop-blur rounded-2xl p-7 hover:bg-white/10 transition-colors">
                <div className="step-number !text-[#80b23f] text-3xl mb-3">{s.n}</div>
                <h3 className="font-display text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/5] shadow-xl">
              <img src="/photos/paysage.jpg" alt="Suisse Energies Renouvelables" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-6 bg-[#80b23f] text-white p-6 rounded-2xl shadow-xl max-w-[240px]">
              <div className="font-display text-3xl">7 cantons</div>
              <div className="text-xs uppercase tracking-widest mt-1 text-white/85">de Suisse romande</div>
            </div>
          </div>
          <div>
            <p className="eyebrow">À propos</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight text-gray-900">Spécialiste des <span className="italic-accent">solutions énergétiques durables.</span></h2>
            <p className="mt-6 text-gray-600 leading-relaxed">En tant que spécialiste pour les solutions énergétiques durables en Suisse romande, Suisse Energies Renouvelables accompagne les particuliers et les entreprises dans leur rénovation énergétique grâce à l'installation d'équipements fiables et performants : panneaux solaires, batteries de stockage, pompes à chaleur.</p>
            <p className="mt-4 text-gray-600 leading-relaxed">Avec notre siège à <strong>Hermance (GE)</strong>, nous intervenons dans les cantons de <strong>Genève, Vaud, Valais, Neuchâtel, Fribourg, Berne et Jura</strong>.</p>
            <div className="grid grid-cols-2 gap-5 mt-8">
              <div className="bg-[#f1f1f1] p-5 rounded-xl">
                <div className="font-display text-3xl text-[#125082]">100%</div>
                <div className="text-xs uppercase tracking-widest mt-1 text-gray-500">Géré en interne</div>
              </div>
              <div className="bg-[#f1f1f1] p-5 rounded-xl">
                <div className="font-display text-3xl text-[#125082]">7</div>
                <div className="text-xs uppercase tracking-widest mt-1 text-gray-500">Cantons couverts</div>
              </div>
            </div>
            <Link to="/a-propos" className="mt-8 btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium">Découvrir notre histoire <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
